import { useMemo, useRef, useState } from 'react';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Upload, X } from 'lucide-react';
import {
  importLeads, previewImportLeads,
  LEAD_AUDIENCE_LISTS, LEAD_AUDIENCE_LIST_LABELS,
  type LeadImportRow, type LeadImportResult, type LeadImportBody, type LeadAudienceList,
} from '@/lib/api';
import styles from './ImportLeadsModal.module.css';

/**
 * Drag-drop lead import (Aaron's surface). Parses CSV (papaparse) and XLSX
 * (SheetJS) client-side, shows a column-mapping preview, collects the batch
 * audience + send-lists + source label + a consent mode, runs a DRY-RUN
 * preview (new vs existing diff, rejected/suppressed counts), then commits.
 * Imported rows never trigger emails. Capped at 10,000 rows per import.
 *
 * Consent modes:
 *   - attest  — the operator attests for the batch and gives an explicit batch
 *               consent source. Every valid row imports.
 *   - per_row — each row must carry its own consent source + a valid consent
 *               date (mapped columns). Rows without a basis are REJECTED, shown
 *               in the preview + result.
 */

const AUDIENCES = [
  'driver_prospect', 'investor', 'newsletter', 'sales_partner',
  'operator_prospect', 'social', 'other',
] as const;
const MAX_ROWS = 10_000;
const BATCH = 1_000;
const SOURCE_RE = /^import_[a-z0-9][a-z0-9_-]*$/;

type ConsentMode = 'attest' | 'per_row';
type TargetField = 'email' | 'name' | 'phone' | 'tags' | 'consent_source' | 'consent_at';

const EMPTY_RESULT: LeadImportResult = {
  total: 0, created: 0, deduped: 0, invalid: 0,
  rejected_no_consent: 0, suppressed_skipped: 0, known_contact_new_source: 0,
};

function slugify(fileName: string): string {
  const base = fileName.toLowerCase().replace(/\.[^.]+$/, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  return base || 'batch';
}

function autodetect(columns: string[], target: TargetField): string {
  const norm = (c: string) => c.toLowerCase().replace(/[^a-z0-9]/g, '');
  const table: Record<TargetField, string[]> = {
    email: ['email', 'mail', 'emailaddress'],
    name: ['name', 'fullname', 'contactname', 'firstname'],
    phone: ['phone', 'mobile', 'tel', 'phonenumber'],
    tags: ['tags', 'tag', 'segment', 'list'],
    consent_source: ['consentsource', 'source', 'origin', 'optinsource', 'howcollected'],
    consent_at: ['consentat', 'consentdate', 'originaltimestamp', 'optindate', 'optin', 'subscribedat', 'signupdate'],
  };
  return columns.find((c) => table[target].some((n) => norm(c).includes(n))) ?? '';
}

interface ParsedFile { columns: string[]; rows: Record<string, string>[] }

export function ImportLeadsModal({
  onClose, onImported,
}: {
  onClose: () => void;
  onImported: () => void;
}) {
  const [parsed, setParsed] = useState<ParsedFile | null>(null);
  const [fileName, setFileName] = useState('');
  const [mapping, setMapping] = useState<Record<TargetField, string>>({
    email: '', name: '', phone: '', tags: '', consent_source: '', consent_at: '',
  });
  const [audience, setAudience] = useState('other');
  const [audiences, setAudiences] = useState<LeadAudienceList[]>([]);
  const [source, setSource] = useState('');
  const [consentMode, setConsentMode] = useState<ConsentMode>('attest');
  const [consent, setConsent] = useState(false);
  const [batchConsentSource, setBatchConsentSource] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [preview, setPreview] = useState<LeadImportResult | null>(null);
  const [result, setResult] = useState<LeadImportResult | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const toggleAudience = (l: LeadAudienceList) =>
    setAudiences((cur) => (cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l]));

  const handleFile = async (file: File) => {
    setParseError(null);
    setResult(null);
    setPreview(null);
    try {
      let columns: string[] = [];
      let rows: Record<string, string>[] = [];
      if (/\.csv$/i.test(file.name)) {
        const text = await file.text();
        const res = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
        rows = res.data;
        columns = res.meta.fields ?? [];
      } else if (/\.xlsx?$/i.test(file.name)) {
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetName = wb.SheetNames[0];
        const ws = sheetName ? wb.Sheets[sheetName] : undefined;
        if (!ws) { setParseError('No sheet found in the workbook.'); return; }
        rows = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '', raw: false });
        columns = rows.length ? Object.keys(rows[0]!) : [];
      } else {
        setParseError('Unsupported file. Upload a .csv or .xlsx file.');
        return;
      }
      if (columns.length === 0 || rows.length === 0) {
        setParseError('No rows detected in the file.');
        return;
      }
      setParsed({ columns, rows });
      setFileName(file.name);
      setMapping({
        email: autodetect(columns, 'email'),
        name: autodetect(columns, 'name'),
        phone: autodetect(columns, 'phone'),
        tags: autodetect(columns, 'tags'),
        consent_source: autodetect(columns, 'consent_source'),
        consent_at: autodetect(columns, 'consent_at'),
      });
      setSource(`import_${slugify(file.name)}`);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : 'Failed to parse the file.');
    }
  };

  const builtRows: LeadImportRow[] = useMemo(() => {
    if (!parsed || !mapping.email) return [];
    const out: LeadImportRow[] = [];
    for (const r of parsed.rows) {
      const email = String(r[mapping.email] ?? '').trim();
      if (!email) continue;
      const name = mapping.name ? String(r[mapping.name] ?? '').trim() : '';
      const phone = mapping.phone ? String(r[mapping.phone] ?? '').trim() : '';
      const tagsRaw = mapping.tags ? String(r[mapping.tags] ?? '').trim() : '';
      const tags = tagsRaw ? tagsRaw.split(/[;,|]/).map((t) => t.trim()).filter(Boolean) : [];
      const consentSource = mapping.consent_source ? String(r[mapping.consent_source] ?? '').trim() : '';
      const consentAt = mapping.consent_at ? String(r[mapping.consent_at] ?? '').trim() : '';
      out.push({
        email,
        ...(name ? { name } : {}),
        ...(phone ? { phone } : {}),
        ...(tags.length ? { tags } : {}),
        ...(consentSource ? { consent_source: consentSource } : {}),
        ...(consentAt ? { consent_at: consentAt } : {}),
      });
    }
    return out;
  }, [parsed, mapping]);

  const overCap = builtRows.length > MAX_ROWS;
  const rowsToSend = overCap ? builtRows.slice(0, MAX_ROWS) : builtRows;
  const sourceValid = SOURCE_RE.test(source);
  // per_row needs a consent-source column mapped; attest needs the checkbox.
  const consentReady = consentMode === 'attest'
    ? consent
    : !!mapping.consent_source;
  const canRun = !!parsed && !!mapping.email && sourceValid && consentReady
    && rowsToSend.length > 0 && !busy;

  const buildBody = (rows: LeadImportRow[]): LeadImportBody => ({
    audience,
    audiences,
    source,
    consent_mode: consentMode,
    ...(consentMode === 'attest'
      ? {
          consent_attested: true,
          ...(batchConsentSource.trim() ? { consent_source: batchConsentSource.trim() } : {}),
        }
      : {}),
    rows,
  });

  /** Loop the row set in batches through preview or commit, aggregating. */
  const runBatches = async (kind: 'preview' | 'commit'): Promise<LeadImportResult> => {
    const agg: LeadImportResult = { ...EMPTY_RESULT };
    for (let i = 0; i < rowsToSend.length; i += BATCH) {
      const chunk = rowsToSend.slice(i, i + BATCH);
      const r = kind === 'preview'
        ? await previewImportLeads(buildBody(chunk))
        : await importLeads(buildBody(chunk));
      agg.total += r.total;
      agg.created += r.created;
      agg.deduped += r.deduped;
      agg.invalid += r.invalid;
      agg.rejected_no_consent += r.rejected_no_consent;
      agg.suppressed_skipped += r.suppressed_skipped;
      agg.known_contact_new_source += r.known_contact_new_source;
    }
    return agg;
  };

  const runPreview = async () => {
    if (!canRun) return;
    setBusy(true);
    setSubmitError(null);
    try {
      setPreview(await runBatches('preview'));
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Preview failed.');
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    if (!canRun) return;
    setBusy(true);
    setSubmitError(null);
    try {
      setResult(await runBatches('commit'));
      onImported();
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : 'Import failed.');
    } finally {
      setBusy(false);
    }
  };

  const renderCounts = (r: LeadImportResult) => (
    <ul className={styles.resultList}>
      <li><strong>{r.created.toLocaleString()}</strong> {result ? 'created' : 'would be created'}</li>
      <li><strong>{r.deduped.toLocaleString()}</strong> already on this list (deduped)</li>
      <li><strong>{r.known_contact_new_source.toLocaleString()}</strong> known contact, new source</li>
      {r.rejected_no_consent > 0 && (
        <li className={styles.rejectLine}><strong>{r.rejected_no_consent.toLocaleString()}</strong> rejected — no consent basis</li>
      )}
      {r.suppressed_skipped > 0 && (
        <li className={styles.rejectLine}><strong>{r.suppressed_skipped.toLocaleString()}</strong> skipped — globally suppressed</li>
      )}
      <li><strong>{r.invalid.toLocaleString()}</strong> invalid rows skipped</li>
    </ul>
  );

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Import leads">
        <div className={styles.header}>
          <h2 className={styles.title}>Import leads</h2>
          <button type="button" className={styles.iconBtn} onClick={onClose} aria-label="Close"><X size={16} /></button>
        </div>

        {result ? (
          <div className={styles.resultBlock}>
            <p className={styles.resultLead}>Import complete.</p>
            {renderCounts(result)}
            <div className={styles.actions}>
              <button type="button" className={styles.primaryBtn} onClick={onClose}>Done</button>
            </div>
          </div>
        ) : !parsed ? (
          <>
            <div
              className={`${styles.dropzone} ${dragOver ? styles.dropzoneActive : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files[0];
                if (f) void handleFile(f);
              }}
              onClick={() => inputRef.current?.click()}
            >
              <Upload size={24} />
              <p className={styles.dropText}>Drag a CSV or XLSX here, or click to choose</p>
              <p className={styles.dropHint}>Up to {MAX_ROWS.toLocaleString()} rows per import. No emails are sent.</p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                className={styles.hiddenInput}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleFile(f); }}
              />
            </div>
            {parseError && <div className={styles.errorBanner}>{parseError}</div>}
          </>
        ) : (
          <>
            <p className={styles.fileLine}>{fileName} · {parsed.rows.length.toLocaleString()} rows</p>

            <p className={styles.sectionLabel}>Map columns</p>
            <div className={styles.mapGrid}>
              {(['email', 'name', 'phone', 'tags', 'consent_source', 'consent_at'] as TargetField[]).map((t) => (
                <label key={t} className={styles.mapRow}>
                  <span className={styles.mapLabel}>
                    {t.replace(/_/g, ' ')}
                    {t === 'email' && <span className={styles.req}> *</span>}
                    {t === 'consent_source' && consentMode === 'per_row' && <span className={styles.req}> *</span>}
                  </span>
                  <select className={styles.select} value={mapping[t]} onChange={(e) => { setMapping((m) => ({ ...m, [t]: e.target.value })); setPreview(null); }}>
                    <option value="">— none —</option>
                    {parsed.columns.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              ))}
            </div>

            <p className={styles.sectionLabel}>Batch</p>
            <div className={styles.mapGrid}>
              <label className={styles.mapRow}>
                <span className={styles.mapLabel}>Origin audience</span>
                <select className={styles.select} value={audience} onChange={(e) => setAudience(e.target.value)}>
                  {AUDIENCES.map((a) => <option key={a} value={a}>{a.replace(/_/g, ' ')}</option>)}
                </select>
              </label>
              <label className={styles.mapRow}>
                <span className={styles.mapLabel}>Source label</span>
                <input className={styles.select} value={source} onChange={(e) => { setSource(e.target.value); setPreview(null); }} aria-invalid={!sourceValid} />
              </label>
            </div>
            {!sourceValid && <div className={styles.warn}>Source must look like import_&lt;slug&gt; (lowercase, no spaces).</div>}

            <span className={styles.mapLabel}>Add to send lists</span>
            <div className={styles.chipRow}>
              {LEAD_AUDIENCE_LISTS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={audiences.includes(l) ? `${styles.chip} ${styles.chipActive}` : styles.chip}
                  aria-pressed={audiences.includes(l)}
                  onClick={() => { toggleAudience(l); setPreview(null); }}
                >
                  {LEAD_AUDIENCE_LIST_LABELS[l]}
                </button>
              ))}
            </div>

            <p className={styles.sectionLabel}>Consent</p>
            <div className={styles.chipRow}>
              <button
                type="button"
                className={consentMode === 'attest' ? `${styles.chip} ${styles.chipActive}` : styles.chip}
                onClick={() => { setConsentMode('attest'); setPreview(null); }}
              >
                Attest for the batch
              </button>
              <button
                type="button"
                className={consentMode === 'per_row' ? `${styles.chip} ${styles.chipActive}` : styles.chip}
                onClick={() => { setConsentMode('per_row'); setPreview(null); }}
              >
                Per-row consent in the file
              </button>
            </div>
            {consentMode === 'attest' ? (
              <>
                <label className={styles.mapRow}>
                  <span className={styles.mapLabel}>Consent source (optional)</span>
                  <input
                    className={styles.select}
                    value={batchConsentSource}
                    placeholder="e.g. 2024 investor opt-in list"
                    onChange={(e) => setBatchConsentSource(e.target.value)}
                  />
                </label>
                <label className={styles.consent}>
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
                  <span>I confirm these contacts consented to be contacted.</span>
                </label>
              </>
            ) : (
              <div className={styles.warn}>
                Per-row mode: each row needs a mapped consent source and a valid consent date.
                Rows without a consent basis are rejected and reported below.
              </div>
            )}

            {!mapping.email && <div className={styles.warn}>Map the email column to continue.</div>}
            {overCap && (
              <div className={styles.warn}>
                File has {builtRows.length.toLocaleString()} rows; only the first {MAX_ROWS.toLocaleString()} will be imported.
              </div>
            )}

            {preview && (
              <div className={styles.previewBlock}>
                <p className={styles.sectionLabel}>Preview (no changes made)</p>
                {renderCounts(preview)}
              </div>
            )}
            {submitError && <div className={styles.errorBanner}>{submitError}</div>}

            <div className={styles.actions}>
              <button type="button" className={styles.secondaryBtn} onClick={() => { setParsed(null); setResult(null); setPreview(null); }}>
                Choose another file
              </button>
              <button type="button" className={styles.secondaryBtn} disabled={!canRun} onClick={runPreview}>
                {busy && !result ? 'Working…' : 'Preview'}
              </button>
              <button type="button" className={styles.primaryBtn} disabled={!canRun} onClick={submit}>
                {busy ? 'Importing…' : `Import ${rowsToSend.length.toLocaleString()}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
